import moment from 'moment'
import { fetchCompany } from '@/api/parameters'
import numeral from 'numeral'
import { lineBreakdownSection } from './lineBreakdownPdf'

export async function generateProductionDashboardPdf(payload) {
  const pdfMake = require("pdfmake/build/pdfmake.js")
  const pdfFonts = require("pdfmake/build/vfs_fonts.js")
  pdfMake.vfs = pdfFonts.pdfMake.vfs

  // Datos de la empresa
  let company = payload.company || {}
  try {
    if (!company.name) {
      company = await fetchCompany()
    }
  } catch (e) {
    company = {}
  }

  const companyName = company.fantasy || company.name || 'Runflow ERP'
  const companyRut = company.rut || ''
  const timezone = payload.serverTimezone || company.current_time_zone || 'America/Santiago'

  const formatNumber = (val, dec = 1) => {
    const num = Number(val || 0)
    return numeral(num).format(dec === 0 ? '0,0' : (dec === 2 ? '0,0.00' : '0,0.0'))
  }

  const asOfFormatted = payload.asOf ? moment(payload.asOf).format('DD/MM/YYYY') : moment().format('DD/MM/YYYY')
  const days = payload.days || 7
  const lineName = payload.lineName || 'Todas las líneas'
  const periodLabel = `Últimos ${days} días · al ${asOfFormatted} · Línea: ${lineName}`
  const lines = payload.lines || []

  const now = moment()
  const emissionDate = now.format('DD/MM/YYYY HH:mm')

  const kpis = payload.kpis || {}
  const areas = payload.areas || []
  const machines = payload.machines || []
  const operators = payload.operators || []
  const daily = payload.daily || []
  const recent = payload.recent || []

  const deltaLabel = (val) => {
    if (val == null) return '—'
    const sign = val > 0 ? '+' : ''
    return `${sign}${val}%`
  }

  const totalM2 = Number(kpis.m2 || 0)
  const pct = (part, tot) => ((Number(part || 0) / Math.max(0.1, tot)) * 100).toFixed(1)

  // Document Definition
  const dd = {
    pageSize: 'LETTER',
    pageOrientation: 'landscape',
    pageMargins: [28, 22, 28, 22],
    footer: function(currentPage, pageCount) {
      return {
        margin: [28, 0, 28, 0],
        columns: [
          { text: 'RUNFLOW ERP · MÓDULO DE PRODUCCIÓN Y CONTROL DE TALLER', fontSize: 7, color: '#94A3B8', bold: true },
          { text: `Página ${currentPage} de ${pageCount}`, alignment: 'right', fontSize: 7, color: '#94A3B8', bold: true }
        ]
      }
    },
    content: [
      // ==========================================
      // PÁGINA 1: RESUMEN EJECUTIVO (ONE-PAGER)
      // ==========================================

      // 1. Header Banner - Midnight Slate (#0F172A)
      {
        table: {
          widths: ['*'],
          body: [
            [
              {
                fillColor: '#0F172A',
                border: [false, false, false, false],
                margin: [16, 12, 16, 12],
                columns: [
                  {
                    width: '*',
                    stack: [
                      {
                        text: 'RUNFLOW · INFORME EJECUTIVO DE PRODUCCIÓN Y TALLER',
                        fontSize: 8,
                        bold: true,
                        color: '#00A650',
                        characterSpacing: 1.5,
                        margin: [0, 0, 0, 3]
                      },
                      {
                        text: `${companyName}`,
                        fontSize: 16,
                        bold: true,
                        color: '#FFFFFF',
                        margin: [0, 0, 0, 3]
                      },
                      {
                        text: `${companyRut ? 'RUT: ' + companyRut + '  ·  ' : ''}Auditoría: ${periodLabel}`,
                        fontSize: 9,
                        color: '#CBD5E1'
                      }
                    ]
                  },
                  {
                    width: 220,
                    alignment: 'right',
                    stack: [
                      {
                        text: 'CONTROL DE OPERACIONES EN PISO',
                        fontSize: 7.5,
                        bold: true,
                        color: '#38BDF8',
                        margin: [0, 0, 0, 6]
                      },
                      {
                        text: `Emisión: ${emissionDate} hrs`,
                        fontSize: 8,
                        color: '#94A3B8',
                        margin: [0, 0, 0, 2]
                      },
                      {
                        text: `Zona Horaria: ${timezone}`,
                        fontSize: 7.5,
                        color: '#64748B'
                      }
                    ]
                  }
                ]
              }
            ]
          ]
        },
        margin: [0, 0, 0, 10],
        layout: {
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0
        }
      },

      // 2. Fila Héroe de 4 KPIs Maestros
      {
        table: {
          widths: ['25%', '25%', '25%', '25%'],
          body: [
            [
              // Card 1: Producción Hoy (North Star)
              {
                fillColor: '#0F172A',
                border: [false, false, false, false],
                margin: [10, 8, 10, 8],
                stack: [
                  { text: 'PRODUCCIÓN DE HOY', fontSize: 7, bold: true, color: '#00A650', characterSpacing: 1 },
                  { text: `${formatNumber(kpis.today_m2, 1)} m²`, fontSize: 15, bold: true, color: '#FFFFFF', margin: [0, 3, 0, 3] },
                  {
                    text: `${formatNumber(kpis.today_qty, 0)} un · ${deltaLabel(kpis.vs_yesterday_pct)} vs ayer`,
                    fontSize: 7.5,
                    color: '#94A3B8'
                  }
                ]
              },
              // Card 2: Período Acumulado
              {
                fillColor: '#F8FAFC',
                border: [false, false, false, false],
                margin: [10, 8, 10, 8],
                stack: [
                  { text: `TOTAL PERÍODO (${days} DÍAS)`, fontSize: 7, bold: true, color: '#64748B', characterSpacing: 1 },
                  { text: `${formatNumber(kpis.m2, 1)} m²`, fontSize: 15, bold: true, color: '#0F172A', margin: [0, 3, 0, 3] },
                  { text: `${formatNumber(kpis.qty, 0)} un · ${deltaLabel(kpis.vs_previous_pct)} vs período anterior`, fontSize: 7.5, color: '#64748B' }
                ]
              },
              // Card 3: Producción Ayer
              {
                fillColor: '#F8FAFC',
                border: [false, false, false, false],
                margin: [10, 8, 10, 8],
                stack: [
                  { text: 'TURNO DE AYER', fontSize: 7, bold: true, color: '#64748B', characterSpacing: 1 },
                  { text: `${formatNumber(kpis.yesterday_m2, 1)} m²`, fontSize: 15, bold: true, color: '#0284C7', margin: [0, 3, 0, 3] },
                  { text: `${formatNumber(kpis.yesterday_qty, 0)} un · ${kpis.yesterday_events || 0} reportes`, fontSize: 7.5, color: '#64748B' }
                ]
              },
              // Card 4: OTs Trabajadas
              {
                fillColor: '#F8FAFC',
                border: [false, false, false, false],
                margin: [10, 8, 10, 8],
                stack: [
                  { text: 'OTs ACTIVAS EN TALLER', fontSize: 7, bold: true, color: '#64748B', characterSpacing: 1 },
                  { text: `${kpis.ots || 0} OTs`, fontSize: 15, bold: true, color: '#0F172A', margin: [0, 3, 0, 3] },
                  { text: `${kpis.events || 0} avances reportados en piso`, fontSize: 7.5, color: '#64748B' }
                ]
              }
            ]
          ]
        },
        margin: [0, 0, 0, 10],
        layout: {
          defaultBorder: false,
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          paddingLeft: () => 3,
          paddingRight: () => 3,
          paddingTop: () => 0,
          paddingBottom: () => 0
        }
      },

      // 2b. Producción por Línea de Negocio (todo el período, sin filtro de línea)
      ...lineBreakdownSection({
        title: 'PRODUCCIÓN POR LÍNEA DE NEGOCIO (TODAS LAS LÍNEAS)',
        rows: lines,
        selectedName: lineName,
        columns: [
          { header: 'LÍNEA', value: (l) => l.name },
          { header: 'M²', width: 90, align: 'right', bold: true, value: (l) => formatNumber(l.m2, 1) },
          { header: 'UNIDADES', width: 90, align: 'right', bold: true, value: (l) => formatNumber(l.qty, 1) },
          { header: 'REPORTES', width: 70, align: 'center', value: (l) => String(l.events || 0) },
          { header: 'OTs', width: 60, align: 'center', value: (l) => String(l.ots || 0) }
        ]
      }),

      // 3. Bloque Intermedio: Comparativa Diaria Reciente + Resumen de Rendimiento
      {
        columns: [
          // Left: Tabla compacta de últimos días
          {
            width: 440,
            stack: [
              {
                text: 'EVOLUCIÓN DIARIA DE PRODUCCIÓN (M² Y AVANCE)',
                fontSize: 8,
                bold: true,
                color: '#475569',
                characterSpacing: 0.8,
                margin: [0, 0, 0, 4]
              },
              {
                table: {
                  headerRows: 1,
                  dontBreakRows: true,
                  widths: [70, 70, 75, 75, 75],
                  body: [
                    [
                      { text: 'DÍA', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC' },
                      { text: 'M² PRODUCIDOS', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' },
                      { text: 'VARIACIÓN DÍA', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' },
                      { text: 'UNIDADES', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' },
                      { text: 'REPORTES', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'center' }
                    ],
                    ...(daily.slice(-8).reverse().map((d) => [
                      {
                        text: d.label + (d.is_today ? ' (Hoy)' : ''),
                        fontSize: 7.5,
                        bold: d.is_today,
                        color: d.is_today ? '#00A650' : '#0F172A'
                      },
                      { text: formatNumber(d.m2, 1), fontSize: 7.5, bold: true, alignment: 'right', color: '#0F172A' },
                      {
                        text: d.vs_prev_pct != null ? `${d.vs_prev_pct > 0 ? '+' : ''}${d.vs_prev_pct}%` : '—',
                        fontSize: 7.5,
                        bold: true,
                        alignment: 'right',
                        color: d.vs_prev_pct > 0 ? '#047857' : (d.vs_prev_pct < 0 ? '#BE123C' : '#64748B')
                      },
                      { text: formatNumber(d.qty, 1), fontSize: 7.5, alignment: 'right', color: '#475569' },
                      { text: String(d.events || 0), fontSize: 7.5, alignment: 'center', color: '#475569' }
                    ]))
                  ]
                },
                layout: {
                  hLineWidth: () => 0.5,
                  vLineWidth: () => 0,
                  hLineColor: () => '#E2E8F0',
                  paddingLeft: () => 6,
                  paddingRight: () => 6,
                  paddingTop: () => 4,
                  paddingBottom: () => 4
                }
              }
            ]
          },
          // Gap: 15 pt
          { width: 15, text: '' },
          // Right: Pulso y Promedios Operativos
          {
            width: 280,
            stack: [
              {
                text: 'PROMEDIOS Y PULSO OPERATIVO',
                fontSize: 8,
                bold: true,
                color: '#475569',
                characterSpacing: 0.8,
                margin: [0, 0, 0, 4]
              },
              {
                table: {
                  widths: ['*'],
                  body: [
                    [
                      {
                        fillColor: '#F8FAFC',
                        border: [false, false, false, false],
                        margin: [10, 8, 10, 8],
                        stack: [
                          { text: 'Rendimiento Promedio Diario', fontSize: 7.5, bold: true, color: '#64748B' },
                          { text: `${formatNumber(kpis.m2 / Math.max(1, days), 1)} m² / día`, fontSize: 13, bold: true, color: '#0F172A', margin: [0, 2, 0, 2] },
                          { text: `Equivalente a ~${formatNumber(kpis.qty / Math.max(1, days), 0)} unidades diarias procesadas`, fontSize: 7, color: '#64748B' }
                        ]
                      }
                    ],
                    [
                      {
                        fillColor: '#F1F5F9',
                        border: [false, false, false, false],
                        margin: [10, 8, 10, 8],
                        stack: [
                          { text: 'Densidad de Reportes en Taller', fontSize: 7.5, bold: true, color: '#64748B' },
                          { text: `${(Number(kpis.events || 0) / Math.max(1, days)).toFixed(1)} avances / día`, fontSize: 13, bold: true, color: '#0284C7', margin: [0, 2, 0, 2] },
                          { text: `Total de ${kpis.events || 0} registros de trazabilidad en bitácora`, fontSize: 7, color: '#64748B' }
                        ]
                      }
                    ]
                  ]
                },
                layout: {
                  defaultBorder: false,
                  hLineWidth: () => 0,
                  vLineWidth: () => 0,
                  paddingLeft: () => 0,
                  paddingRight: () => 0,
                  paddingTop: () => 2,
                  paddingBottom: () => 2
                }
              }
            ]
          }
        ],
        margin: [0, 0, 0, 10]
      },

      // 4. Bloque Inferior: Top Áreas, Top Máquinas y Top Operadores (3 columnas equilibradas)
      {
        columns: [
          // Columna 1: Top 5 Áreas
          {
            width: 240,
            stack: [
              {
                text: 'TOP ÁREAS DE PRODUCCIÓN',
                fontSize: 8,
                bold: true,
                color: '#475569',
                characterSpacing: 0.8,
                margin: [0, 0, 0, 4]
              },
              {
                table: {
                  headerRows: 1,
                  dontBreakRows: true,
                  widths: ['*', 55, 35],
                  body: [
                    [
                      { text: 'ÁREA', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC' },
                      { text: 'M²', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' },
                      { text: '%', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' }
                    ],
                    ...(areas.slice(0, 5).length ? areas.slice(0, 5).map((a) => [
                      { text: a.name || 'Sin área', fontSize: 7.5, bold: true, color: '#0F172A' },
                      { text: formatNumber(a.m2, 1), fontSize: 7.5, bold: true, alignment: 'right', color: '#0F172A' },
                      { text: `${pct(a.m2, totalM2)}%`, fontSize: 7.5, alignment: 'right', color: '#64748B' }
                    ]) : [
                      [{ text: 'Sin registros', colSpan: 3, alignment: 'center', fontSize: 7.5, color: '#94A3B8' }, {}, {}]
                    ])
                  ]
                },
                layout: {
                  hLineWidth: () => 0.5,
                  vLineWidth: () => 0,
                  hLineColor: () => '#E2E8F0',
                  paddingLeft: () => 4,
                  paddingRight: () => 4,
                  paddingTop: () => 3.5,
                  paddingBottom: () => 3.5
                }
              }
            ]
          },
          // Gap: 15 pt
          { width: 15, text: '' },
          // Columna 2: Top 5 Máquinas
          {
            width: 240,
            stack: [
              {
                text: 'TOP MÁQUINAS / EQUIPOS',
                fontSize: 8,
                bold: true,
                color: '#475569',
                characterSpacing: 0.8,
                margin: [0, 0, 0, 4]
              },
              {
                table: {
                  headerRows: 1,
                  dontBreakRows: true,
                  widths: ['*', 55, 35],
                  body: [
                    [
                      { text: 'MÁQUINA', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC' },
                      { text: 'M²', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' },
                      { text: '%', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' }
                    ],
                    ...(machines.slice(0, 5).length ? machines.slice(0, 5).map((m) => [
                      { text: m.name || 'Sin equipo', fontSize: 7.5, bold: true, color: '#0F172A' },
                      { text: formatNumber(m.m2, 1), fontSize: 7.5, bold: true, alignment: 'right', color: '#0F172A' },
                      { text: `${pct(m.m2, totalM2)}%`, fontSize: 7.5, alignment: 'right', color: '#64748B' }
                    ]) : [
                      [{ text: 'Sin registros', colSpan: 3, alignment: 'center', fontSize: 7.5, color: '#94A3B8' }, {}, {}]
                    ])
                  ]
                },
                layout: {
                  hLineWidth: () => 0.5,
                  vLineWidth: () => 0,
                  hLineColor: () => '#E2E8F0',
                  paddingLeft: () => 4,
                  paddingRight: () => 4,
                  paddingTop: () => 3.5,
                  paddingBottom: () => 3.5
                }
              }
            ]
          },
          // Gap: 15 pt
          { width: 15, text: '' },
          // Columna 3: Top 5 Operadores
          {
            width: 210,
            stack: [
              {
                text: 'TOP OPERADORES EN PISO',
                fontSize: 8,
                bold: true,
                color: '#475569',
                characterSpacing: 0.8,
                margin: [0, 0, 0, 4]
              },
              {
                table: {
                  headerRows: 1,
                  dontBreakRows: true,
                  widths: ['*', 55, 35],
                  body: [
                    [
                      { text: 'OPERADOR', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC' },
                      { text: 'M²', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' },
                      { text: '%', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' }
                    ],
                    ...(operators.slice(0, 5).length ? operators.slice(0, 5).map((o) => [
                      { text: o.name || 'Sin operador', fontSize: 7.5, bold: true, color: '#0F172A' },
                      { text: formatNumber(o.m2, 1), fontSize: 7.5, bold: true, alignment: 'right', color: '#0F172A' },
                      { text: `${pct(o.m2, totalM2)}%`, fontSize: 7.5, alignment: 'right', color: '#64748B' }
                    ]) : [
                      [{ text: 'Sin registros', colSpan: 3, alignment: 'center', fontSize: 7.5, color: '#94A3B8' }, {}, {}]
                    ])
                  ]
                },
                layout: {
                  hLineWidth: () => 0.5,
                  vLineWidth: () => 0,
                  hLineColor: () => '#E2E8F0',
                  paddingLeft: () => 4,
                  paddingRight: () => 4,
                  paddingTop: () => 3.5,
                  paddingBottom: () => 3.5
                }
              }
            ]
          }
        ]
      },

      // ========================================================
      // PÁGINA 2: ANEXO DE AUDITORÍA Y TRAZABILIDAD RECIENTE
      // ========================================================
      ...(recent && recent.length ? [
        { text: '', pageBreak: 'before' },
        // Cabecera Secundaria de Anexo
        {
          table: {
            widths: ['*'],
            body: [
              [
                {
                  fillColor: '#F8FAFC',
                  border: [false, false, false, false],
                  margin: [12, 8, 12, 8],
                  columns: [
                    {
                      text: 'ANEXO DE TRAZABILIDAD: ÚLTIMOS REPORTES DE AVANCE EN PISO',
                      fontSize: 10,
                      bold: true,
                      color: '#0F172A'
                    },
                    {
                      text: `${companyName} · Período auditado: ${periodLabel}`,
                      fontSize: 8,
                      color: '#64748B',
                      alignment: 'right'
                    }
                  ]
                }
              ]
            ]
          },
          margin: [0, 0, 0, 10],
          layout: {
            paddingLeft: () => 0,
            paddingRight: () => 0,
            paddingTop: () => 0,
            paddingBottom: () => 0
          }
        },

        // Tabla Detallada de Movimientos Recientes
        {
          table: {
            headerRows: 1,
            dontBreakRows: true,
            widths: [55, 45, '*', 110, 100, 60, 50],
            body: [
              [
                { text: 'HORA', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC' },
                { text: 'OT', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'center' },
                { text: 'PRODUCTO / PROCESO', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC' },
                { text: 'ÁREA / MÁQUINA', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC' },
                { text: 'OPERADOR', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC' },
                { text: 'M²', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' },
                { text: 'UNID.', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' }
              ],
              ...recent.map((row) => [
                { text: row.at ? moment(row.at).format('DD/MM HH:mm') : '—', fontSize: 7.5, color: '#64748B' },
                { text: row.ot ? `OT #${row.ot}` : '—', fontSize: 7.5, bold: true, color: '#00A650', alignment: 'center' },
                {
                  stack: [
                    { text: row.product || 'Sin producto', fontSize: 7.5, bold: true, color: '#0F172A' },
                    { text: row.process || '', fontSize: 7, color: '#64748B' }
                  ]
                },
                {
                  stack: [
                    { text: row.area || '—', fontSize: 7.5, color: '#0F172A' },
                    { text: row.equipment || '', fontSize: 7, color: '#64748B' }
                  ]
                },
                { text: row.operator || '—', fontSize: 7.5, color: '#334155' },
                { text: formatNumber(row.m2, 2), fontSize: 7.5, bold: true, alignment: 'right', color: '#0F172A' },
                { text: formatNumber(row.qty, 1), fontSize: 7.5, alignment: 'right', color: '#64748B' }
              ])
            ]
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0,
            hLineColor: () => '#E2E8F0',
            paddingLeft: () => 6,
            paddingRight: () => 6,
            paddingTop: () => 4,
            paddingBottom: () => 4
          }
        }
      ] : [])
    ]
  }

  const fileName = `Reporte_Ejecutivo_Produccion_${days}dias_${moment().format('YYYYMMDD')}.pdf`
  pdfMake.createPdf(dd).download(fileName)
}
